import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { filter, tap } from 'rxjs/operators';
import { ApBaseComponent } from '../ap-base-component/index';
import { ApAlertService } from './ap-alert.service';
import { ApAlert, ApAlertType } from './ApAlert';

export const apAlertClassByType = new Map<ApAlertType | undefined, string>([
  [ApAlertType.Success, 'alert alert-success alert-dismissable'],
  [ApAlertType.Info, 'alert alert-info alert-dismissable'],
  [ApAlertType.Error, 'alert alert-danger alert-dismissable'],
  [ApAlertType.Warning, 'alert alert-warning alert-dismissable'],
  [undefined, ''],
]);

@Component({
  selector: 'ap-alert',
  styleUrls: ['./ap-alert.component.less'],
  templateUrl: 'ap-alert.component.html',
})
export class ApAlertComponent extends ApBaseComponent implements OnInit, OnDestroy {
  private idSet = new Set<string>([
    this.alertService.defaultId,
  ]);
  @Input()
  set ids(val: string[]) {
    this.idSet = new Set(val);
  }

  @Input() fade = true;
  @Output() alertRemoved = new EventEmitter<ApAlert>();

  // only show a max number of alerts at a time,
  // so they don't stack up like crazy and take up most of a tile or page
  @Input() maxAlerts = 50;

  alerts: ApAlert[] = [];
  alertErrors: ApAlert[] = [];

  readonly apAlertClassByType = apAlertClassByType;

  constructor(private alertService: ApAlertService) {
    super();
  }

  ngOnInit() {
    this.alertService.onAlert().pipe(
      filter((x) => this.idSet.has(x.id)),
      tap(alert => {
        this.addNewAlert(alert);
      }),
      this.takeUntilDestroy(),
    ).subscribe();
    this.alertService.onClearAlert().pipe(
      filter((x) => this.idSet.has(x.id)),
      tap(alert => {
        this.removeAlert(alert);
      }),
      this.takeUntilDestroy(),
    ).subscribe();
    this.alertService.onClearErrors().pipe(
      filter((x) => this.idSet.has(x)),
      tap(clearId => {
        this.addNewErrorAlert(new ApAlert({ id: clearId }));
      }),
      this.takeUntilDestroy(),
    ).subscribe();
  }

  removeAlert(alert: ApAlert) {
    if (alert.type === ApAlertType.Error) {
      this.removeAlertError(alert);
      return;
    }
    if (!this.alerts.includes(alert)) return;

    if (this.fade) {
      const alertToRemove = this.alerts.find((x) => x === alert);
      if (alertToRemove) {
        alertToRemove.fade = true;
      }

      setTimeout(() => {
        this.alerts = this.alerts.filter((x) => x !== alert);
      }, 250);
    } else {
      this.alerts = this.alerts.filter((x) => x !== alert);
    }

    this.alertRemoved.emit(alert);
  }

  removeAlertError(alert: ApAlert) {
    if (!this.alertErrors.includes(alert)) return;

    if (this.fade) {
      const alertToRemove = this.alertErrors.find((x) => x === alert);
      if (alertToRemove) {
        alertToRemove.fade = true;
      }

      setTimeout(() => {
        this.alertErrors = this.alertErrors.filter((x) => x !== alert);
      }, 250);
    } else {
      this.alertErrors = this.alertErrors.filter((x) => x !== alert);
    }

    this.alertRemoved.emit(alert);
  }

  private addNewAlert(alert: ApAlert) {
    if (alert.type === ApAlertType.Error) {
      this.addNewErrorAlert(alert);
      return;
    }
    if (!alert.message) {
      this.alerts = this.alerts.filter(x => x.id !== alert.id);
      return;
    }

    if (this.alerts.length >= this.maxAlerts) {
      // if we hit the max, don't add more
      return;
    }

    this.alerts.push(alert);

    if (alert.autoClose && alert.autoCloseTimeout) {
      setTimeout(() => this.removeAlert(alert), alert.autoCloseTimeout);
    }
  }
  private addNewErrorAlert(alert: ApAlert) {
    if (!alert.message) {
      this.alertErrors = this.alertErrors.filter(x => x.id !== alert.id);
      return;
    }

    if (this.alertErrors.length >= this.maxAlerts) {
      // if we hit the max, don't add more
      return;
    }

    this.alertErrors.push(alert);

    if (alert.autoClose && alert.autoCloseTimeout) {
      setTimeout(() => this.removeAlertError(alert), alert.autoCloseTimeout);
    }
  }
}
